import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import ReactImageGallery from "react-image-gallery";
import { useMediaQuery } from "@mui/material";

const Hero = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const isMobile = useMediaQuery("(max-width:575px)");

  const imageGalleryRef = useRef();

  const getData = async () => {
    setLoading(true);
    if (error) setError(false);
    await axios
      .get("get-banner")
      .then((res) => {
        let data = res?.data?.banner?.map((element) => ({
          ...element,
          src: element?.url,
          id: element?._id,
        }));

        if (isMobile) {
          setBanners(data?.filter((banner) => banner?.ismobile));
        } else {
          setBanners(data?.filter((banner) => !banner?.ismobile));
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  };

  useEffect(() => {
    getData();
  }, [isMobile]);

  const showSkeleton = loading || (!error && banners.length === 0);

  return (
    <div className="gph-hero">
      <section className="home-hero-details-section gph-hero-head">
        <div className="home-hero-details-inner-first mt-50">
          <h1>
            UAE Trusted <span className="highlight">Digital Gift Card</span> Marketplace
          </h1>
        </div>
      </section>

      <div className="home-banner-wrapper gph-hero-banner-wrap">
        {showSkeleton ? (
          <div className="gph-hero-skel" role="status" aria-label="Loading banner">
            <div className="gph-hero-skel-inner">
              <div className="gph-hero-skel-shine" />
              <div className="gph-hero-skel-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </div>
              <div className="gph-hero-skel-line gph-hero-skel-line-lg" />
              <div className="gph-hero-skel-line gph-hero-skel-line-md" />
            </div>
          </div>
        ) : error ? (
          <div className="gph-hero-error" role="alert">
            <div className="gph-hero-error-card">
              <div className="gph-hero-error-icon" aria-hidden="true">!</div>
              <h4>Something went wrong</h4>
              <p>We couldn't load the banners right now.</p>
              <button type="button" className="gph-hero-error-btn" onClick={getData}>
                Try again
              </button>
            </div>
          </div>
        ) : (
          <ReactImageGallery
            ref={imageGalleryRef}
            showPlayButton={false}
            autoPlay={true}
            items={banners?.map((image) => ({
              original: image?.url,
              thumbnail: image?.url,
            }))}
            slideDuration={600}
            showBullets={false}
            showThumbnails={false}
            showFullscreenButton={false}
            showNav={false}
            renderItem={(src) => (
              <img
                src={src.original}
                alt={`home-banner-images-${src?.original}`}
                className="banner-image"
              />
            )}
          />
        )}
      </div>

      <style>{`
        .gph-hero {
          position: relative;
          background: linear-gradient(180deg, #07060a 0%, #0b0b0c 60%, #0e0a05 100%);
          padding-bottom: 8px;
          overflow: hidden;
        }
        .gph-hero::before,
        .gph-hero::after {
          content: "";
          position: absolute;
          width: 480px;
          height: 480px;
          border-radius: 50%;
          filter: blur(140px);
          opacity: 0.35;
          pointer-events: none;
          z-index: 0;
        }
        .gph-hero::before {
          background: rgba(212, 168, 87, 0.22);
          top: -180px;
          left: -160px;
        }
        .gph-hero::after {
          background: rgba(212, 168, 87, 0.14);
          bottom: -200px;
          right: -160px;
        }

        .gph-hero-head {
          position: relative;
          z-index: 1;
          background: transparent !important;
        }

        .gph-hero-banner-wrap {
          position: relative;
          z-index: 1;
          background-color: transparent !important;
        }

        /* Dark skeleton */
        .gph-hero-skel {
          width: 95%;
          height: 65vh;
          margin: 20px auto 0;
          border-radius: 20px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.2);
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .gph-hero-skel-inner {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          width: min(420px, 70%);
          color: #d4a857;
        }
        .gph-hero-skel-icon {
          width: 76px;
          height: 76px;
          border-radius: 18px;
          background: rgba(212, 168, 87, 0.08);
          border: 1px solid rgba(212, 168, 87, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #d4a857;
          opacity: 0.85;
        }
        .gph-hero-skel-line {
          height: 12px;
          border-radius: 999px;
          background: linear-gradient(
            90deg,
            rgba(212, 168, 87, 0.08) 0%,
            rgba(212, 168, 87, 0.18) 50%,
            rgba(212, 168, 87, 0.08) 100%
          );
          background-size: 200% 100%;
          animation: gph-hero-shimmer 1.6s linear infinite;
        }
        .gph-hero-skel-line-lg { width: 75%; }
        .gph-hero-skel-line-md { width: 45%; }
        .gph-hero-skel-shine {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            115deg,
            transparent 30%,
            rgba(212, 168, 87, 0.07) 48%,
            rgba(212, 168, 87, 0.12) 50%,
            rgba(212, 168, 87, 0.07) 52%,
            transparent 70%
          );
          background-size: 220% 100%;
          animation: gph-hero-sweep 2.4s linear infinite;
          z-index: 0;
        }
        @keyframes gph-hero-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes gph-hero-sweep {
          0% { background-position: 200% 0; }
          100% { background-position: -100% 0; }
        }
        @media (max-width: 767px) {
          .gph-hero-skel { height: 38vh; border-radius: 14px; }
          .gph-hero-skel-icon { width: 60px; height: 60px; }
        }

        /* Error card */
        .gph-hero-error {
          width: 95%;
          height: 65vh;
          margin: 20px auto 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .gph-hero-error-card {
          padding: 30px 36px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.25);
          border-radius: 18px;
          text-align: center;
          color: #e6e8ec;
          max-width: 380px;
          width: 100%;
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.4);
        }
        .gph-hero-error-icon {
          width: 48px;
          height: 48px;
          margin: 0 auto 14px;
          border-radius: 50%;
          background: rgba(255, 122, 122, 0.12);
          border: 1px solid rgba(255, 122, 122, 0.4);
          color: #ff7a7a;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 22px;
        }
        .gph-hero-error-card h4 {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 19px;
          color: #fff;
          margin: 0 0 6px;
        }
        .gph-hero-error-card p {
          font-size: 13px;
          color: #9aa0a8;
          margin: 0 0 18px;
        }
        .gph-hero-error-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 22px;
          background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
          color: #0b0b0c;
          border: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.25s ease;
        }
        .gph-hero-error-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 22px rgba(212, 168, 87, 0.3);
        }
        @media (max-width: 767px) {
          .gph-hero-error { height: 38vh; }
          .gph-hero-error-card { padding: 22px 20px; }
        }
      `}</style>
    </div>
  );
};

export default Hero;
